<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import QuoteModel from '@models/Quote';
import QuotePositions from '@views/quotes/positions/positions';
import HeaderComponent from '@components/HeaderComponent';
import utility from '@mixins/utility';

export default {
  page() {
    return {
      title: this.$t('titles.quotes'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    QuotePositions,
    HeaderComponent,
  },
  mixins: [utility],
  props: {
    pageTitle: {
      required: true,
      default: '',
      type: String,
    },
  },
  data() {
    return {
      sortBy: 'formatted_date',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'formatted_date', label: this.$t('table_labels.date'), sortable: true },
        { key: 'company_name', label: this.$t('table_labels.company'), sortable: true },
        { key: 'vip_name', label: this.$t('table_labels.vip'), sortable: true },
        { key: 'location', label: this.$t('table_labels.location'), sortable: true },
        { key: 'contact_name', label: this.$t('table_labels.contact'), sortable: true },
        { key: 'organizer', label: this.$t('table_labels.organizer'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          this.$refs.asyncTable.refresh();
        });
      },
      immediate: false,
      deep: true,
    },
  },
  mounted() {
    window.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.$refs.asyncTable.refresh();
      }
    },
    goToEditQuote(quoteId) {
      const routeData = this.$router.resolve({ name: 'quotes.edit', params: { id: quoteId } });
      window.open(routeData.href, '_blank');
    },
    itemsProvider(ctx) {
      return QuoteModel.orderBy(this.sortDir + ctx.sortBy)
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.quote') }))) {
        this.$wait.start('quote processing');
        const quote = new QuoteModel({ id });
        await quote.delete();
        this.$wait.end('quote processing');
        this.$refs.asyncTable.refresh();
      }
    },
    async duplicate(id) {
      if (confirm(this.$t('alerts.duplicate_entity', { entity: this.$t('entities.quote') }))) {
        this.$wait.start('quote processing');
        this.$http
          .post(`admin/quotes/${id}/duplicate`)
          .then((response) => {
            this.$wait.end('quote processing');
          })
          .catch(() => this.$wait.end('quote processing'));
        // add delay to ensure the new quote is available
        setTimeout(() => {  
          this.$refs.asyncTable.refresh();
        }, 1000);
      }
    },
    exportPdf(quote) {
      this.$wait.start(`downloading-quote-${quote.id}`);
      this.$http
          .get(`admin/quotes/${quote.id}/export`, {
                params: { lang: quote.language },
                responseType: 'blob'
          })
          .then((response) => {
          this.$wait.end(`downloading-quote-${quote.id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName !== '' ? fileName : `Quote #${quote.id}.pdf`;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading-quote-${quote.id}`));
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    updateQuotePositions(quotePositions, item, key) {
      this.$set(quotePositions, key, item);
    },
  },
};
</script>
<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="pageTitle" :button-text="$t('titles.create_quote')" @button-pressed="$router.push({ name: 'quotes.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="4" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [
                    $t('search.entities.id'),
                    $t('search.entities.vip'),
                    $t('search.entities.company'),
                    $t('search.entities.contact'),
                    $t('search.entities.organizer'),
                  ].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="quotes-index" class="position-relative mt-4">
      <BlockingContainer v-if="$wait.is('quote processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        responsive
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
        table-class="no-min-width"
        style="overflow: inherit"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(vip_name)="row">
          <span v-if="row.item.vip">{{ row.item.vip.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(company_name)="row">
          <span v-if="row.item.company">{{ row.item.company.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(contact_name)="row">
          <span v-if="row.item.contact">{{ row.item.contact.first_name }} {{ row.item.contact.last_name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(organizer)="row">
          <span v-if="row.item.organizer">{{ row.item.organizer.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(location)="row">
          <span v-if="row.item.location">{{ row.item.location.name }}</span>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(actions)="row">
          <b-dropdown variant="outline-primary" dropleft no-caret size="sm">
            <template #button-content>
              <FontAwesomeIcon :icon="['fas', 'ellipsis-h']" />
            </template>

            <b-dropdown-item variant="primary" @click="goToEditQuote(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
              <small>&nbsp;{{ $t('actions.edit') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="success" :disabled="$wait.is(`downloading${row.item.id}-export`)" @click="exportPdf(row.item)">
              <FontAwesomeIcon :icon="['fas', 'file-export']" />
              <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}-export_pdf`)" class="ml-1" icon="spinner" spin />
              <small>&nbsp;{{ $t('actions.export_pdf') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="primary" @click.stop="duplicate(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'copy']" />
              <small>&nbsp;{{ $t('actions.duplicate') }}</small>
            </b-dropdown-item>

            <b-dropdown-item variant="danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
              <small>&nbsp;{{ $t('actions.delete') }}</small>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
        <template v-slot:row-details="row">
          <div v-if="row.item.quote_positions.length" class="mb-2">
            <QuotePositions
              :quote="row.item"
              :quote-positions="row.item.quote_positions"
              @updated="updateQuotePositions(row.item.quote_positions, ...arguments)"
            />
          </div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <div class="d-flex justify-content-start align-items-left mt-2">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
          <div class="text-secondary d-flex align-items-center justify-content-center mb-3 px-4">
            {{  $t('infos.showing_results', {
              start: (currentPage - 1) * perPage + 1,
              end: Math.min(currentPage * perPage, totalRows),
              total: totalRows
            }) }}
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
